import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import tryRefreshAccessToken from '@entities/auth/lib/tryRefreshAccessToken';
import useAuthStore from '@entities/auth/model/useAuthStore';
import {
  useCompanyListQuery,
  useLoginMutation,
  useLoginCompleteMutation,
} from '@features/auth/hooks';
import decodeJwt from '@shared/lib/decodeJwt';
import { getLastCompanyCode } from '@shared/lib/cookie';
import Alert from '@shared/components/atoms/Alert';
import { ReactComponent as PonyLogo } from '@shared/assets/images/ponylink-dark-logo.svg';
import LoginLogo from '@shared/assets/images/login-logo.png';
import Modal from '@shared/components/atoms/Modal/Modal';
import GlobalFooter from '@shared/components/organisms/GlobalFooter/GlobalFooter';
import eyeClosedIcon from '@shared/assets/images/eye-closed_icon.svg';
import eyeOpenIcon from '@shared/assets/images/eye-open_icon.svg';

import ChangePassword from '@pages/login/components/ChangePassword';
import FindAccount from '@pages/login/components/FindAccount';
import { useIsMobile } from '@shared/hooks';
import { useIsApp } from '@shared/hooks';
import MobileLogin from '@pages/login/components/mobile/MobileLogin';
import useMobileLoginView from '@features/auth/hooks/useMobileLoginView';
import { LOGIN_VIEW } from '@features/auth/hooks/useMobileLoginView';
import MobileFindAccount from '@pages/login/components/mobile/MobileFindAccount';

import styles from '@pages/login/LoginPage.module.scss';

/**
 * LoginPage
 * - 로그인 화면
 * - 아이디 찾기 / 비밀번호 찾기 / 비밀번호 재설정 모달 제어
 * - 모든 알림(Alert)은 이 컴포넌트에서 단일 관리
 */
export default function LoginPage() {
  // 모바일 화면 감지
  const isMobile = useIsMobile();
  const isApp = useIsApp();

  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const toggleLoginPassword = useCallback(() => {
    setShowLoginPassword((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isApp) {
      document.body.classList.add('is-app');
      document.body.classList.remove('is-web');
    } else {
      document.body.classList.add('is-web');
      document.body.classList.remove('is-app');
    }

    return () => {
      document.body.classList.remove('is-app');
      document.body.classList.remove('is-web');
    };
  }, [isApp]);

  const MODAL_TITLE_MAP = {
    findId: '아이디 찾기',
    findPassword: '비밀번호 찾기',
    resetPassword: '비밀번호 재설정',
    emailVerification: '이메일 인증',
  };
  /** =======================
   * 로그인 입력 상태
   ======================= */
  const [userId, setUserId] = useState('');
  const [passwd, setPasswd] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  /** =======================
   * 모달 / 플로우 상태
   ======================= */
  const [modalType, setModalType] = useState(null); // findId | findPassword | resetPassword
  const [resetFindId, setResetFindId] = useState(false); // FindAccount 초기화 트리거

  const [resetEmail, setResetEmail] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  /** =======================
   * Alert 상태 (단일 UX)
   ======================= */
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [alertMode, setAlertMode] = useState('alert'); // 'alert' | 'confirm'
  const [alertConfirmText, setAlertConfirmText] = useState('예');
  const [alertCancelText, setAlertCancelText] = useState('아니요');
  const [alertOnConfirm, setAlertOnConfirm] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const clearToken = useAuthStore((state) => state.clearToken);
  const hasTriedSilentLoginRef = useRef(false);
  const hasAppliedCookieRef = useRef(false);

  // 관리자 모드 감지
  const isAdminMode = location.pathname.startsWith('/admin/login');

  const { mutate: completeLogin } = useLoginCompleteMutation({
    onSuccess: (data) => {
      sessionStorage.setItem('isSessionActive', 'true');
      sessionStorage.removeItem('preventAutoLogin');
      localStorage.setItem('isRememberMe', String(rememberMe));

      const { admin, passwordChangeRecommended } = data.resultData;

      // 일반 로그인과 동일하게 이동
      navigate('/approval/approver', {
        state: { showPasswordChangeAlert: passwordChangeRecommended },
        replace: true,
      });
    },
    onError: (err) => {
      showAlert(err.friendlyMsg || '로그인에 실패했습니다.');
    },
  });

  /** =======================
   * 공통 유틸
   ======================= */

  // Alert 노출 공통 함수
  const showAlert = useCallback((message, options = {}) => {
    setAlertMessage(message);

    setAlertMode(options.mode ?? 'alert');
    setAlertConfirmText(options.confirmText ?? '예');
    setAlertCancelText(options.cancelText ?? '아니요');
    setAlertOnConfirm(() => options.onConfirm ?? null);

    setAlertOpen(true);
  }, []);

  useEffect(
    function () {
      // 이미 로그인 상태면 로그인 페이지 진입 시 적절한 페이지로 이동
      if (token) {
        if (isAdminMode) {
          // 관리자 모드인 경우 admin 값 확인 (user 또는 토큰 payload)
          if (user?.admin === 1) {
            navigate('/admin/documents', { replace: true });
          } else if (decodeJwt(token)?.admin === 1) {
            // 토큰에는 관리자이나 user 미로드 등 → 알럿 없이 로그인 폼 유지 (외부 IP 이메일 인증 정책 유지)
            return;
          } else {
            // 일반 직원이 관리자 로그인 페이지에 접속한 경우
            showAlert('관리자만 접근 가능합니다.');
          }
        } else {
          // 일반 모드
          navigate('/approval/approver', { replace: true });
        }
        return;
      }

      // rememberMe=true로 발급된 Refresh Token(HttpOnly 쿠키)이 남아있다면
      // 로그인 페이지에서 조용히 토큰 재발급을 시도하여 자동 로그인 처리
      // 단, 이미 시도했거나 token이 있으면 시도하지 않음
      if (hasTriedSilentLoginRef.current) {
        return;
      }
      hasTriedSilentLoginRef.current = true;

      // preventAutoLogin 플래그가 있으면 (동시접속 차단 등으로 튕긴 경우) 자동 로그인 시도 안 함
      if (sessionStorage.getItem('preventAutoLogin') === 'true') {
        return;
      }

      // PC 웹에서는 사용자가 명시적으로 "로그인 유지"를 선택한 경우에만 자동 로그인(silent login) 시도
      // 모바일 환경이 아니고, 로그인 유지가 false이면 시도하지 않음
      const isRememberMe = localStorage.getItem('isRememberMe') === 'true';
      if (!isApp && !isRememberMe) {
        return;
      }

      async function trySilentLogin() {
        try {
          const newAccessToken = await tryRefreshAccessToken();

          if (newAccessToken) {
            setAccessToken(newAccessToken);
            // 자동 로그인 시에는 일반 페이지로 이동 (관리자 모드가 아닌 경우)
            if (!isAdminMode) {
              navigate('/approval/approver', { replace: true });
            }
            // 관리자 모드인 경우는 AdminProtectedRoute에서 처리
          }
        } catch (error) {
          // Refresh Token이 없거나 만료된 경우는 정상 케이스이므로 무시합니다.
        }
      }

      trySilentLogin();
    },
    [navigate, setAccessToken, token, isAdminMode, user, showAlert],
  );

  // input onChange 중복 제거
  const handleChange = useCallback((setter) => (e) => setter(e.target.value), []);

  /** =======================
   * 회사 목록 조회
   ======================= */
  const { data: companyList = [], isLoading } = useCompanyListQuery({
    select(data) {
      return data?.resultData?.companyList ?? [];
    },
  });

  /** =======================
   * 쿠키에 저장된 마지막 회사 코드로 회사 선택 초기화
   * - 회사 목록 로드 후 최초 1회만 적용(마운트당)
   * - 쿠키 값이 목록에 있을 때만 자동 선택
   ======================= */
  useEffect(
    function applyLastCompanyFromCookie() {
      if (hasAppliedCookieRef.current || companyList.length === 0) return;
      const lastCode = getLastCompanyCode();
      if (!lastCode) return;
      const exists = companyList.some((c) => String(c.companyCode ?? '') === String(lastCode));
      if (exists) {
        setCompanyCode(lastCode);
        hasAppliedCookieRef.current = true;
      }
    },
    [companyList],
  );

  const handleEmailVerificationSuccess = ({ emailCode, requestId }) => {
    completeLogin({ userId, companyCode, emailCode, requestId });
  };
  /** =======================
   * 로그인 처리
   ======================= */
  const { mutate: login, isPending } = useLoginMutation({
    onSuccess(data) {
      const { result_code, resultData, message } = data;

      //  이메일 인증 필요 상태
      if (resultData?.loginStatus === 'PENDING_EMAIL_VERIFICATION') {
        // 이메일 인증 모달 열기
        setModalType('emailVerification'); // 새 모달 타입
        // showAlert(message || '이메일 인증이 필요합니다.');
        return;
      }

      sessionStorage.setItem('isSessionActive', 'true');
      sessionStorage.removeItem('preventAutoLogin');
      localStorage.setItem('isRememberMe', String(rememberMe));

      const { admin, passwordChangeRecommended } = data.resultData;

      // 관리자 모드에서 로그인한 경우
      if (isAdminMode) {
        if (admin === 1) {
          // 관리자 계정인 경우 관리자 페이지로 이동
          navigate('/admin/documents', {
            state: {
              showPasswordChangeAlert: passwordChangeRecommended,
            },
          });
        } else {
          // 일반 직원이 관리자 로그인 페이지에서 로그인한 경우
          showAlert('관리자만 로그인 가능합니다.');
          // 로그인 페이지 유지
        }
      } else {
        // 일반 모드에서 로그인한 경우
        navigate('/approval/approver', {
          state: {
            showPasswordChangeAlert: passwordChangeRecommended,
          },
        });
      }
    },
    onError(error) {
      showAlert(error.friendlyMsg || `로그인 실패: ${error.message}`);
    },
  });

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!companyCode) {
        showAlert('회사를 선택해주세요.');
        return;
      }

      if (!userId.trim() || !passwd.trim()) {
        showAlert('아이디와 비밀번호를 입력해주세요.');
        return;
      }

      login({ userId, passwd, companyCode, rememberMe });
    },
    [companyCode, userId, passwd, rememberMe, login, showAlert],
  );

  /** =======================
   * 모달 제어
   ======================= */

  // 모달 닫기 + FindAccount 초기화
  const handleCloseModal = () => {
    setModalType(null);
    setResetFindId((prev) => !prev);
  };

  /** =======================
   * 아이디 찾기 성공
   ======================= */
  const handleFindIdSuccess = ({ result }) => {
    const resultData = result?.resultData;
    const users = resultData?.user;

    if (!Array.isArray(users) || users.length === 0) {
      showAlert('아이디 찾기 결과를 불러올 수 없습니다.');
      return;
    }

    handleCloseModal();

    // 중복 제거 없이 그대로 id만 추출
    const ids = users.map((u) => u.id);

    const message = (
      <div className={styles.findIdAlert}>
        <div className={styles.findIdTitle}>아이디 찾기</div>
        <div className={styles.findIdBox}>
          <div className={styles.findIdCard}>
            {ids.map((id, index) => (
              <>
                <div className={styles.findIdContent}>
                  <div className={styles.findIdLabel}>아이디</div>
                  <div key={`${id}-${index}`} className={styles.findIdValue}>
                    {id}
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
      </div>
    );

    showAlert(message, {
      mode: 'confirm',
      confirmText: '비밀번호 찾기',
      cancelText: '닫기',
      onConfirm: () => {
        setModalType('findPassword');
      },
    });
  };

  /** =======================
   * 비밀번호 찾기 성공
   ======================= */
  const handleFindPasswordSuccess = ({ result, email }) => {
    const resultData = result?.resultData;
    if (!resultData) {
      showAlert('비밀번호 찾기 결과를 불러올 수 없습니다.');
      return;
    }

    setResetEmail(email);
    setResetToken(resultData.resetToken);
    setModalType('resetPassword');
  };

  /** =======================
   * 비밀번호 재설정 완료
   ======================= */
  const handleResetPasswordSuccess = () => {
    handleCloseModal();
    showAlert('비밀번호가 성공적으로 변경되었습니다.');
  };

  const handleResendSuccess = useCallback(() => {
    showAlert('인증 메일이 재전송 되었습니다.');
  }, [showAlert]);

  const { view, openLogin, openFindId, openFindPassword, openResetPassword } = useMobileLoginView();

  if (isMobile) {
    return (
      <>
        {(() => {
          switch (view) {
            case LOGIN_VIEW.FIND_ID:
              return (
                <MobileFindAccount
                  mode="findId"
                  onBack={openLogin}
                  onSuccess={(data) => {
                    if (data?.mode === 'resetSuccess') {
                      showAlert('비밀번호가 성공적으로 변경되었습니다.');
                    } else {
                      handleFindIdSuccess(data);
                    }
                  }}
                  onError={showAlert}
                  onResendSuccess={handleResendSuccess}
                  companyList={companyList}
                />
              );

            case LOGIN_VIEW.FIND_PASSWORD:
              return (
                <MobileFindAccount
                  mode="findPassword"
                  onBack={openLogin}
                  companyList={companyList}
                  onSuccess={(data) => {
                    if (data?.mode === 'resetSuccess') {
                      showAlert('비밀번호가 성공적으로 변경되었습니다.');
                    } else {
                      handleFindPasswordSuccess(data);
                    }
                  }}
                  onError={showAlert}
                  onResendSuccess={handleResendSuccess}
                />
              );

            // case LOGIN_VIEW.RESET:
            //   return (
            //     <MobileChangePassword
            //       email={resetEmail}
            //       resetToken={resetToken}
            //       onBack={openLogin}
            //       onSuccess={handleResetPasswordSuccess}
            //       onError={showAlert}
            //     />
            //   );

            case LOGIN_VIEW.LOGIN:
            default:
              return (
                <MobileLogin
                  companyList={companyList}
                  isLoading={isLoading}
                  isPending={isPending}
                  userId={userId}
                  passwd={passwd}
                  companyCode={companyCode}
                  rememberMe={rememberMe}
                  onChangeUserId={handleChange(setUserId)}
                  onChangePasswd={handleChange(setPasswd)}
                  onChangeCompanyCode={handleChange(setCompanyCode)}
                  onToggleRememberMe={(e) => setRememberMe(e.target.checked)}
                  onSubmit={handleSubmit}
                  onFindId={openFindId}
                  onFindPassword={openFindPassword}
                />
              );
          }
        })()}
        {/* 모바일용 전역 Alert */}
        <Alert
          isOpen={alertOpen}
          message={alertMessage}
          mode={alertMode}
          confirmText={alertConfirmText}
          cancelText={alertCancelText}
          onConfirm={alertOnConfirm}
          onClose={() => setAlertOpen(false)}
          className={styles.mobileAlert}
        />
        {modalType === 'emailVerification' && !isApp && (
          <Modal
            isOpen={modalType !== null}
            onClose={handleCloseModal}
            showCloseButton={false}
            title={MODAL_TITLE_MAP[modalType]}
            classes={{
              modalHeader: styles.centerHeader,
            }}
            size="medium"
          >
            <FindAccount
              mode="emailVerification"
              resetSignal={resetFindId}
              onSuccess={handleEmailVerificationSuccess}
              onError={showAlert}
              onCancel={handleCloseModal}
              onResendSuccess={handleResendSuccess}
              loginUserId={userId}
              loginCompanyCode={companyCode}
            />
          </Modal>
        )}
      </>
    );
  }

  return (
    <div className={styles.loginContainer}>
      <div className={styles.visualSection}>
        <img src={LoginLogo} alt="login visual" className={styles.visualImg} />
      </div>
      <div className={styles.loginCard}>
        <div className={styles.leftArea}>
          <PonyLogo className={styles.ponyLogo} />
          <p className={styles.subTitle}>전자문서 결재 서비스</p>
          {isAdminMode && <div className={styles.subTitle}>ADMIN</div>}
        </div>

        <div className={styles.rightArea}>
          <form onSubmit={handleSubmit} className={styles.formWrapper}>
            <span className={styles.formTitle}>로그인</span>
            <select
              value={companyCode}
              onChange={handleChange(setCompanyCode)}
              disabled={isLoading}
              className={styles.select}
            >
              <option value="" disabled>
                회사 선택
              </option>
              {/* map 콜백을 ({ company, index })로 잘못 사용하려고해서 key/value가 undefined가 됐음  ㅇㅋ? 테스트하는 습관... 같이 노력하즈아아*/}
              {companyList.map(function (company, index) {
                // key는 항상 고유해야 하므로 index를 포함하여 중복 방지
                const uniqueKey = company.companyCode
                  ? `${company.companyCode}-${index}`
                  : `company-${index}`;
                return (
                  <option key={uniqueKey} value={company.companyCode ?? ''}>
                    {company.company ?? ''}
                  </option>
                );
              })}
            </select>

            <input
              type="text"
              placeholder="아이디"
              value={userId}
              onChange={handleChange(setUserId)}
              autoComplete="off"
            />
            <div className={styles.passwordWrapper}>
              <input
                type={showLoginPassword ? 'text' : 'password'}
                placeholder="비밀번호"
                value={passwd}
                autoComplete="current-password"
                onChange={handleChange(setPasswd)}
                className={styles.passwordInput}
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={toggleLoginPassword}
                aria-label={showLoginPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                <img
                  src={showLoginPassword ? eyeOpenIcon : eyeClosedIcon}
                  alt=""
                  className={styles.eyeIcon}
                />
              </button>
            </div>
            <button type="submit" className={styles.loginBtn} disabled={isPending || isLoading}>
              로그인
            </button>

            <label className={styles.keepLogin}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              로그인 유지
            </label>

            <div className={styles.links}>
              <button
                className={styles.findBtn}
                type="button"
                onClick={() => setModalType('findId')}
              >
                아이디 찾기
              </button>
              <span>|</span>
              <button
                className={styles.findBtn}
                type="button"
                onClick={() => setModalType('findPassword')}
              >
                비밀번호 찾기
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 하단 영역 */}
      <div className={styles.bottomSection}>
        <Modal
          isOpen={modalType !== null}
          onClose={handleCloseModal}
          showCloseButton={false}
          closeOnOverlayClick={false}
          title={MODAL_TITLE_MAP[modalType]}
          classes={{
            modalHeader: styles.centerHeader,
          }}
          size="medium"
        >
          {modalType === 'findId' && (
            <FindAccount
              mode="findId"
              resetSignal={resetFindId}
              onSuccess={handleFindIdSuccess}
              onError={showAlert}
              onCancel={handleCloseModal}
              onResendSuccess={handleResendSuccess}
            />
          )}

          {modalType === 'findPassword' && (
            <FindAccount
              mode="findPassword"
              companyList={companyList}
              resetSignal={resetFindId}
              onSuccess={handleFindPasswordSuccess}
              onError={showAlert}
              onCancel={handleCloseModal}
              onResendSuccess={handleResendSuccess}
            />
          )}

          {modalType === 'resetPassword' && (
            <ChangePassword
              onSuccess={handleResetPasswordSuccess}
              resetToken={resetToken}
              email={resetEmail}
              onError={showAlert}
              onCancel={handleCloseModal}
            />
          )}

          {modalType === 'emailVerification' && (
            <FindAccount
              mode="emailVerification"
              resetSignal={resetFindId}
              onSuccess={handleEmailVerificationSuccess}
              onError={showAlert}
              onCancel={handleCloseModal}
              onResendSuccess={handleResendSuccess}
              loginUserId={userId}
              loginCompanyCode={companyCode}
            />
          )}
        </Modal>

        <Alert
          isOpen={alertOpen}
          message={alertMessage}
          mode={alertMode}
          confirmText={alertConfirmText}
          cancelText={alertCancelText}
          onConfirm={alertOnConfirm}
          onClose={() => setAlertOpen(false)}
        />
        <GlobalFooter />
      </div>
    </div>
  );
}
